/** Types the part-coding upload picker accepts. TIFF and BMP stay out of the picker. */
export const PART_CODING_FILE_ACCEPT =
	'.txt,.csv,.xlsx,.xls,.docx,.pdf,.png,.jpg,.jpeg,.webp,.eml,.msg';

export const PART_CODING_MAX_FILE_BYTES = 10 * 1024 * 1024;

const ALLOWED_EXTENSIONS = new Set([
	'txt',
	'csv',
	'xlsx',
	'xls',
	'docx',
	'pdf',
	'png',
	'jpg',
	'jpeg',
	'webp',
	'eml',
	'msg'
]);

const CONTENT_TYPES: Record<string, string> = {
	txt: 'text/plain',
	csv: 'text/csv',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	xls: 'application/vnd.ms-excel',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	pdf: 'application/pdf',
	png: 'image/png',
	jpg: 'image/jpeg',
	jpeg: 'image/jpeg',
	webp: 'image/webp',
	eml: 'message/rfc822',
	msg: 'application/vnd.ms-outlook'
};

export const UNSUPPORTED_FILE_MESSAGE =
	"That file type isn't supported. Use PDF, Excel, Word, CSV, text, an image (PNG, JPG, WebP) or an email file (.eml, .msg).";

export function fileExtension(name: string): string {
	const dot = name.lastIndexOf('.');
	if (dot < 0 || dot === name.length - 1) return '';
	return name.slice(dot + 1).toLowerCase();
}

/** Null when the file may be uploaded. */
export function unsupportedFileMessage(name: string): string | null {
	return ALLOWED_EXTENSIONS.has(fileExtension(name))
		? null
		: UNSUPPORTED_FILE_MESSAGE;
}

/** Null when the file is within the limit. */
export function fileTooLargeMessage(
	bytes: number,
	limit = PART_CODING_MAX_FILE_BYTES
): string | null {
	if (bytes <= limit) return null;
	const megabytes = bytes / (1024 * 1024);
	const shown =
		megabytes >= 10 ? String(Math.round(megabytes)) : megabytes.toFixed(1);
	return `That file is ${shown} MB; the limit is 10 MB.`;
}

export function contentTypeFor(name: string, browserType: string): string {
	const fromName = CONTENT_TYPES[fileExtension(name)];
	if (fromName) return fromName;
	return browserType || 'application/octet-stream';
}

/** Strips a `data:...;base64,` prefix. A bare base64 string is returned as-is. */
export function base64FromDataUrl(value: string): string {
	const comma = value.indexOf(',');
	if (comma < 0) return value;
	return value.slice(comma + 1);
}

export function readFileAsDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = (): void => {
			resolve(typeof reader.result === 'string' ? reader.result : '');
		};
		reader.onerror = (): void => {
			reject(reader.error ?? new Error('Could not read the file.'));
		};
		reader.readAsDataURL(file);
	});
}

export function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024)
		return `${Math.max(1, Math.round(bytes / 1024))} KB`;
	const megabytes = bytes / (1024 * 1024);
	return `${megabytes >= 10 ? Math.round(megabytes) : megabytes.toFixed(1)} MB`;
}
